import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('love4vegan_user_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('love4vegan_user_token', newToken);
    // Also set admin token if user is admin
    if (userData.is_admin) {
      localStorage.setItem('love4vegan_admin_token', newToken);
      localStorage.setItem('love4vegan_admin_user', userData.email);
    }
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('love4vegan_user_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('love4vegan_user_token');
    localStorage.removeItem('love4vegan_admin_token');
    localStorage.removeItem('love4vegan_admin_user');
    setToken(null);
    setUser(null);
  };

  const updateFavorites = (recipeId, add = true) => {
    if (!user) return;
    setUser(prev => ({
      ...prev,
      favorites: add
        ? [...(prev.favorites || []), recipeId]
        : (prev.favorites || []).filter(id => id !== recipeId)
    }));
  };

  return (
    <UserContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      isAdmin: user?.is_admin || false,
      login,
      register,
      logout,
      updateFavorites
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
